import { useEffect } from 'react';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])';

// Open dialogs, innermost last. Only the top one handles Escape and Tab, so a
// detail dialog opened from another one closes on its own.
const openDialogs = [];

/**
 * Modal focus handling: moves focus into the dialog, keeps Tab inside it,
 * closes on Escape and hands focus back to the opener on unmount.
 * `onClose` should be stable, or focus jumps back on every render.
 */
const useDialogFocus = (dialogRef, initialFocusRef, onClose) => {
  useEffect(() => {
    const token = {};
    openDialogs.push(token);
    const opener = document.activeElement;
    (initialFocusRef.current || dialogRef.current)?.focus();

    const handleKeyDown = (event) => {
      if (openDialogs[openDialogs.length - 1] !== token) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;

      const focusable = dialogRef.current.querySelectorAll(FOCUSABLE);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      openDialogs.splice(openDialogs.indexOf(token), 1);
      if (opener && typeof opener.focus === 'function') opener.focus();
    };
  }, [dialogRef, initialFocusRef, onClose]);
};

export default useDialogFocus;
